import { InfiniteQueryObserverBaseResult } from "@tanstack/react-query";
import { useState, useRef, useCallback, useEffect } from "react";

const useInfiniteScroll = (
  fetchNextPage?: InfiniteQueryObserverBaseResult["fetchNextPage"],
) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const initialLoadAttemptedRef = useRef(false);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (target.isIntersecting && fetchNextPage && !isLoadingMore) {
        setIsLoadingMore(true);
        fetchNextPage().finally(() => {
          setIsLoadingMore(false);
        });
      }
    },
    [fetchNextPage, isLoadingMore],
  );

  useEffect(() => {
    if (
      !initialLoadAttemptedRef.current &&
      loadingRef.current &&
      fetchNextPage
    ) {
      initialLoadAttemptedRef.current = true;

      const observer = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsLoadingMore(true);
          fetchNextPage().finally(() => {
            setIsLoadingMore(false);
          });
        }
      });

      observer.observe(loadingRef.current);

      return () => observer.disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNextPage, loadingRef.current]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (loadingRef.current && tabListRef.current && fetchNextPage) {
      observerRef.current = new IntersectionObserver(handleObserver, {
        root: tabListRef.current,
        rootMargin: "200px",
        threshold: 0.1,
      });

      observerRef.current.observe(loadingRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [fetchNextPage, handleObserver, loadingRef, tabListRef]);

  return { isLoadingMore, loadingRef, tabListRef };
};

export default useInfiniteScroll;
